import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  InvalidAmountError,
  parseUsdcToBaseUnits,
  LISTING_CATEGORY_VALUES,
  LISTING_CATEGORY_LABELS,
  type ListListingsQuery,
} from "@hybrid-escrow/shared";
import { useListings } from "../hooks/useListings";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { apiClient } from "../lib/api-client";
import { ListingCard } from "../components/ListingCard";
import { EmptyState, ErrorBanner, LoadingState, describeError } from "../components/States";
import { usePageMeta } from "../lib/seo";

type SortOption = NonNullable<ListListingsQuery["sort"]>;

const SORT_LABELS: Record<SortOption, string> = {
  NEWEST: "Newest first",
  PRICE_ASC: "Price: low to high",
  PRICE_DESC: "Price: high to low",
};

/** Everything except cursor — cursor is tracked separately below since changing a filter always
 *  resets pagination back to the first page. */
type Filters = Omit<ListListingsQuery, "cursor" | "limit">;

const DEFAULT_FILTERS: Filters = { sort: "NEWEST" };

export function BrowsePage() {
  usePageMeta({
    title: "Browse listings",
    description:
      "Browse digital goods for sale on MarketOnBase, a marketplace secured by a non-custodial smart contract on Base.",
    path: "/",
  });

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [minPriceInput, setMinPriceInput] = useState("");
  const [maxPriceInput, setMaxPriceInput] = useState("");
  const [priceError, setPriceError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pages, setPages] = useState<string[]>([]); // cursor breadcrumb, for a simple "back" affordance
  const [saveSearchLabel, setSaveSearchLabel] = useState("");
  const { isSignedIn } = useAuthPrincipal();

  const saveSearch = useMutation({
    mutationFn: () =>
      apiClient.createSavedSearch({
        label: saveSearchLabel.trim(),
        q: filters.q,
        category: filters.category,
        maxPriceBaseUnits: filters.maxPriceBaseUnits,
      }),
    onSuccess: () => setSaveSearchLabel(""),
  });

  const { data, isLoading, isError, error, refetch, isFetching } = useListings({
    ...filters,
    cursor,
    limit: 24,
  });

  function applyFilters(next: Filters) {
    setFilters(next);
    setCursor(undefined);
    setPages([]);
  }

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    setPriceError(null);

    let minPriceBaseUnits: string | undefined;
    let maxPriceBaseUnits: string | undefined;
    try {
      if (minPriceInput.trim()) minPriceBaseUnits = parseUsdcToBaseUnits(minPriceInput.trim()).toString();
      if (maxPriceInput.trim()) maxPriceBaseUnits = parseUsdcToBaseUnits(maxPriceInput.trim()).toString();
    } catch (err) {
      setPriceError(err instanceof InvalidAmountError ? err.message : "Enter a valid USDC amount.");
      return;
    }
    if (minPriceBaseUnits && maxPriceBaseUnits && BigInt(minPriceBaseUnits) > BigInt(maxPriceBaseUnits)) {
      setPriceError("Minimum price can't be higher than the maximum.");
      return;
    }

    applyFilters({
      ...filters,
      q: searchInput.trim() || undefined,
      minPriceBaseUnits,
      maxPriceBaseUnits,
    });
  }

  function handleClearFilters() {
    setSearchInput("");
    setMinPriceInput("");
    setMaxPriceInput("");
    setPriceError(null);
    applyFilters(DEFAULT_FILTERS);
  }

  function loadMore() {
    if (data?.nextCursor) {
      setPages((p) => [...p, cursor ?? ""]);
      setCursor(data.nextCursor);
    }
  }

  const hasActiveFilters =
    Boolean(filters.category) || Boolean(filters.q) || Boolean(filters.minPriceBaseUnits) || Boolean(filters.maxPriceBaseUnits);

  return (
    <div className="stack">
      <div>
        <h1>Browse listings</h1>
        <p className="muted">Digital goods sold with a secure smart contract on Base.</p>
      </div>

      <form className="card stack" onSubmit={handleSearchSubmit}>
        <div className="row">
          <div className="field" style={{ flex: 2, marginBottom: 0 }}>
            <label htmlFor="search-q">Search</label>
            <input
              id="search-q"
              type="text"
              placeholder="Search titles and descriptions…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="filter-category">Category</label>
            <select
              id="filter-category"
              value={filters.category ?? ""}
              onChange={(e) =>
                applyFilters({
                  ...filters,
                  category: (e.target.value || undefined) as Filters["category"],
                })
              }
            >
              <option value="">All categories</option>
              {LISTING_CATEGORY_VALUES.map((value) => (
                <option key={value} value={value}>
                  {LISTING_CATEGORY_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="filter-sort">Sort by</label>
            <select
              id="filter-sort"
              value={filters.sort ?? "NEWEST"}
              onChange={(e) => applyFilters({ ...filters, sort: e.target.value as SortOption })}
            >
              {(Object.keys(SORT_LABELS) as SortOption[]).map((value) => (
                <option key={value} value={value}>
                  {SORT_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="filter-min-price">Min price (USDC)</label>
            <input
              id="filter-min-price"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={minPriceInput}
              onChange={(e) => setMinPriceInput(e.target.value)}
              style={{ maxWidth: 120 }}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="filter-max-price">Max price (USDC)</label>
            <input
              id="filter-max-price"
              type="text"
              inputMode="decimal"
              placeholder="Any"
              value={maxPriceInput}
              onChange={(e) => setMaxPriceInput(e.target.value)}
              style={{ maxWidth: 120 }}
            />
          </div>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <button type="submit" className="btn btn-primary">
              Apply
            </button>
            {hasActiveFilters && (
              <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
        {priceError && (
          <p className="field-error-text" role="alert">
            {priceError}
          </p>
        )}

        {isSignedIn && hasActiveFilters && (
          <div className="row" style={{ alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}>
              <label htmlFor="save-search-label">Save this search</label>
              <input
                id="save-search-label"
                type="text"
                placeholder={'Name it, e.g. "Cheap game accounts"'}
                value={saveSearchLabel}
                onChange={(e) => setSaveSearchLabel(e.target.value)}
                maxLength={100}
              />
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!saveSearchLabel.trim() || saveSearch.isPending}
              onClick={() => saveSearch.mutate()}
            >
              {saveSearch.isPending ? "Saving…" : saveSearch.isSuccess ? "Saved ✓" : "Save"}
            </button>
          </div>
        )}
        {saveSearch.isSuccess && (
          <p className="text-sm muted">
            We'll email you when a new listing matches. Manage saved searches from your{" "}
            <Link to="/watchlist">watchlist</Link>.
          </p>
        )}
        {saveSearch.isError && (
          <p className="field-error-text" role="alert">
            {describeError(saveSearch.error)}
          </p>
        )}
      </form>

      {isLoading && <LoadingState label="Loading listings…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}

      {data && data.items.length === 0 && (
        <EmptyState title="No listings match">
          {hasActiveFilters ? (
            <p>Try widening your search or clearing filters.</p>
          ) : (
            <p>Check back soon, or sign in to list a digital product for sale.</p>
          )}
        </EmptyState>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="grid grid--listings">
            {data.items.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
          <div className="row" style={{ justifyContent: "center" }}>
            {pages.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const prev = pages[pages.length - 1];
                  setPages((p) => p.slice(0, -1));
                  setCursor(prev || undefined);
                }}
              >
                Back
              </button>
            )}
            {data.nextCursor && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadMore}
                disabled={isFetching}
              >
                {isFetching ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
